// features/intelligence/tabs/dashboard.js
// ═══════════════════════════════════════════════════════════
// DASHBOARD — the morning intelligence layer.
// Reads from connected data sources and shows the current
// state of the domain environment in 8 organized sections.
// Every section has a Practitioner Lens at the bottom that
// expands into user-specific interpretation.
//
// Layer 1 (this file): all sections rendered from
// lib/intelligence/sample-trading-domain.js. Layer 2 will
// swap the data import for live API responses; the section
// components don't change shape. Layer 3 swaps the lens text
// from sample to Claude-generated.
//
// 8 section groups, ~16 cards total. Order locked by spec:
//   01 — Market Regime               (full width, teal-top)
//   02 — Pre-Market (Futures + Movers)              (50/50)
//   03 — Event Calendar + Earnings                  (50/50)
//   04 — Breadth + Sector Rotation                  (40/60)
//   05 — Volatility + Sentiment                     (50/50)
//   06 — Macro (Rates + Currency + Commodities)     (1/3 each)
//   07 — Economic Scoreboard         (full width, 4×3 grid)
//   08 — Intelligence Feed + Community Pulse        (50/50)
// ═══════════════════════════════════════════════════════════

import { TRADING_DOMAIN } from '../../../lib/intelligence/sample-trading-domain';
import SectionCard from '../components/section-card';
import PractitionerLens from '../components/practitioner-lens';
import { createContext, useContext, useState } from 'react';

// Phase 2 — DashboardTab now accepts a `bundle` prop. The
// bundle has the same shape as TRADING_DOMAIN but with live
// values overlaid section-by-section by build-trading-bundle.
// Each section reads from this context instead of a module
// constant, so the tab re-renders cleanly when the parent
// fetches a refreshed bundle every 60s.
const BundleContext = createContext(TRADING_DOMAIN);

// Helper for any section that wants to render its freshness
// timestamp inline. Sections without live data fall back to
// "Sample data" copy via the bundle's _freshness map.
function formatLocalTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_e) { return ''; }
}

export default function DashboardTab({ bundle, onLensPathway }) {
  const D = bundle || TRADING_DOMAIN;
  return (
    <BundleContext.Provider value={D}>
    <div className="di-scroll">

      {/* 01 — MARKET REGIME (full width, teal accent) */}
      <RegimeSection onLensPathway={onLensPathway} />

      {/* 02 — PRE-MARKET (50/50) */}
      <div className="di-two-col di-col-50-50">
        <FuturesSection onLensPathway={onLensPathway} />
        <MoversSection onLensPathway={onLensPathway} />
      </div>

      {/* 03 — EVENT CALENDAR + EARNINGS */}
      <div className="di-two-col di-col-50-50">
        <EventsSection onLensPathway={onLensPathway} />
        <EarningsSection onLensPathway={onLensPathway} />
      </div>

      {/* 04 — BREADTH + SECTORS (40/60) */}
      <div className="di-two-col di-col-40-60">
        <BreadthSection onLensPathway={onLensPathway} />
        <SectorsSection onLensPathway={onLensPathway} />
      </div>

      {/* 05 — VOLATILITY + SENTIMENT (50/50) */}
      <div className="di-two-col di-col-50-50">
        <VolatilitySection onLensPathway={onLensPathway} />
        <SentimentSection onLensPathway={onLensPathway} />
      </div>

      {/* 06 — MACRO (1/3 each) */}
      <div className="di-three-col">
        <RatesSection onLensPathway={onLensPathway} />
        <CurrencySection onLensPathway={onLensPathway} />
        <CommoditiesSection onLensPathway={onLensPathway} />
      </div>

      {/* 07 — ECONOMIC SCOREBOARD (full width) */}
      <ScoreboardSection onLensPathway={onLensPathway} />

      {/* 08 — INTELLIGENCE FEED + COMMUNITY (50/50) */}
      <div className="di-two-col di-col-50-50">
        <FeedSection onLensPathway={onLensPathway} />
        <CommunitySection />
      </div>

    </div>
    </BundleContext.Provider>
  );
}

// ── 01 — Market Regime ─────────────────────────────────────
function RegimeSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const r = D.regime;
  const fresh = D._freshness?.regime;
  return (
    <div className="di-section">
      <div className="di-sec-header">
        <div className="di-sec-label">Market Regime</div>
        <div className="di-sec-right">
          {fresh?.source === 'live' && !fresh.isStale && <div className="di-sec-live">Live</div>}
          <div className="di-sec-time">
            {fresh?.fetchedAt ? `Classified ${formatLocalTime(fresh.fetchedAt)}` : 'Sample data'}
          </div>
        </div>
      </div>
      <div className="di-regime-wrap">
        <div className="di-regime-top">
          <div className="di-reg-left">
            <div className="di-reg-eyebrow">{r.eyebrow}</div>
            <div className="di-reg-title">{r.title}<em>{r.titleAccent}</em></div>
            <div className="di-reg-sub" dangerouslySetInnerHTML={{ __html: r.sub }} />
            <div className="di-reg-tags">
              {r.tags.map((t, i) => (
                <div key={i} className={`di-reg-tag ${t.kind}`}>{t.label}</div>
              ))}
            </div>
          </div>
          <div className="di-reg-chips">
            {r.chips.map((c, i) => (
              <div key={i} className={`di-reg-chip ${c.tone}`}>
                <span className="di-rc-lbl">{c.label}</span>
                <span className="di-rc-v">{c.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <PractitionerLens lens={r.lens} onPathway={onLensPathway} />
    </div>
  );
}

// ── 02a — US Futures + International ───────────────────────
function FuturesSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const f = D.futures;
  return (
    <SectionCard label="US Futures + International" freshness={D._freshness?.futures} lens={f.lens} onLensPathway={onLensPathway}>
      <div className="di-futures-grid">
        {f.us.map((c, i) => (
          <div key={i} className="di-fc">
            <div className="di-fc-t">{c.name}</div>
            <div className="di-fc-v">{c.value}</div>
            <div className={`di-fc-c ${c.dir}`}>{c.change}</div>
          </div>
        ))}
      </div>
      <div className="di-intl-grid">
        {f.intl.map((r, i) => (
          <div key={i} className="di-ig-row">
            <span className="di-ig-n">{r.name}</span>
            <span className={`di-ig-v ${r.dir}`}>{r.change}</span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ── 02b — Pre-Market Movers ────────────────────────────────
function MoversSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const m = D.movers;
  return (
    <SectionCard label="Pre-Market Movers" live lens={m.lens} onLensPathway={onLensPathway}>
      <div className="di-sec-lbl">Gap Up</div>
      {m.up.map((g, i) => (
        <div key={i} className="di-gap-item">
          <span className="di-gi-ticker">{g.ticker}</span>
          <span className="di-gi-reason">{g.reason}</span>
          <span className="di-gi-chg up">{g.change}</span>
        </div>
      ))}
      <div className="di-sec-lbl" style={{ marginTop: 10 }}>Gap Down</div>
      {m.down.map((g, i) => (
        <div key={i} className="di-gap-item">
          <span className="di-gi-ticker">{g.ticker}</span>
          <span className="di-gi-reason">{g.reason}</span>
          <span className="di-gi-chg dn">{g.change}</span>
        </div>
      ))}
    </SectionCard>
  );
}

// ── 03a — Event Calendar ───────────────────────────────────
function EventsSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const e = D.events;
  return (
    <div className="di-section">
      <div className="di-sec-header">
        <div className="di-sec-label">Event Calendar — Today</div>
      </div>
      <div className="di-sec-body">
        {e.today.map((ev, i) => {
          if (ev.level === 'fomc') {
            return (
              <div key={i} className="di-ev-item di-ev-fomc">
                <div className="di-ev-time" style={{ color: '#c46050' }}>{ev.time}</div>
                <div className="di-ev-dot hi" style={{ background: '#c46050', boxShadow: '0 0 4px rgba(196,96,80,0.4)' }} />
                <div>
                  <div className="di-ev-name" style={{ color: '#c8c4e0', fontWeight: 500 }}>{ev.name}</div>
                  <div className="di-ev-meta" style={{ color: '#c46050' }}>{ev.meta}</div>
                </div>
              </div>
            );
          }
          return (
            <div key={i} className="di-ev-item">
              <div className="di-ev-time">{ev.time}</div>
              <div className={`di-ev-dot ${ev.level}`} />
              <div>
                <div className="di-ev-name">{ev.name}</div>
                <div className="di-ev-meta">{ev.meta}</div>
              </div>
            </div>
          );
        })}
        <div className="di-sec-lbl" style={{ marginTop: 10 }}>This Week</div>
        {e.week.map((ev, i) => (
          <div key={i} className="di-ev-item">
            <div className="di-ev-time" style={{ color: '#555566' }}>{ev.time}</div>
            <div className={`di-ev-dot ${ev.level}`} />
            <div>
              <div className="di-ev-name">{ev.name}</div>
            </div>
          </div>
        ))}
      </div>
      <PractitionerLens lens={e.lens} onPathway={onLensPathway} />
    </div>
  );
}

// ── 03b — Earnings Calendar ────────────────────────────────
function EarningsSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const e = D.earnings;
  return (
    <SectionCard label="Earnings Calendar" lens={e.lens} onLensPathway={onLensPathway}>
      <EarnGroup label="Pre-Market Today" items={e.pre} />
      <EarnGroup label="After Close Today" items={e.post} marginTop={8} />
      <EarnGroup label="This Week — Key Names" items={e.week} marginTop={8} />
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #1e1e2a' }}>
        {e.season.map((s, i) => (
          <div key={i} className="di-dr">
            <div className="di-dl">{s.label}</div>
            <div className={`di-dv ${s.tone}`}>{s.value}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
function EarnGroup({ label, items, marginTop }) {
  return (
    <>
      <div className="di-earn-lbl" style={marginTop ? { marginTop } : undefined}>{label}</div>
      {items.map((e, i) => (
        <div key={i} className="di-earn-item">
          <div>
            <div className="di-ei-t">{e.ticker}</div>
            <div className="di-ei-n">{e.name}</div>
          </div>
          <div className="di-ei-r">
            <div className="di-ei-eps">{e.eps}</div>
            <div className="di-ei-mv" style={e.moveTone === 'sage' ? { color: '#7a9e82' } : undefined}>{e.move}</div>
          </div>
        </div>
      ))}
    </>
  );
}

// ── 04a — Market Breadth ───────────────────────────────────
function BreadthSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const b = D.breadth;
  return (
    <SectionCard label="Market Breadth" live lens={b.lens} onLensPathway={onLensPathway}>
      <div className="di-br-grid">
        {b.stats.map((s, i) => (
          <div key={i} className="di-br-stat">
            <div className="di-brs-lbl">{s.label}</div>
            <div className={`di-brs-v ${s.tone}`}>{s.value}</div>
            <div className="di-brs-sub">{s.sub}</div>
          </div>
        ))}
      </div>
      {b.bars.map((bar, i) => (
        <div key={i} className="di-br-bar-row">
          <div className="di-brl">{bar.label}</div>
          <div className="di-brw">
            <div className={`di-brb ${bar.tone}`} style={{ width: `${bar.pct}%` }} />
          </div>
          <div className={`di-brv ${bar.tone}`}>{bar.value}</div>
        </div>
      ))}
    </SectionCard>
  );
}

// ── 04b — Sector Rotation ──────────────────────────────────
function SectorsSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const s = D.sectors;
  return (
    <SectionCard
      label="Sector Rotation — S&P 500 · Today"
      freshness={D._freshness?.sectors}
      lens={s.lens}
      onLensPathway={onLensPathway}
      bodyStyle={{ padding: '10px 16px' }}
    >
      {s.rows.map((r, i) => (
        <div key={i} className="di-sec-row">
          <div className="di-sr-rk">{r.rank}</div>
          <div className="di-sr-nm">{r.name}</div>
          <div className="di-sr-bw">
            <div className="di-sr-ctr" />
            <div className={`di-sr-bar ${r.dir === 'up' ? 'pos' : 'neg'}`} style={{ width: `${r.pct}%` }} />
          </div>
          <div className={`di-sr-v ${r.dir}`}>{r.value}</div>
        </div>
      ))}
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #1e1e2a' }}>
        {s.summary.map((row, i) => (
          <div key={i} className="di-dr">
            <div className="di-dl">{row.label}</div>
            <div className={`di-dv ${row.tone}`}>{row.value}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ── 05a — Volatility ───────────────────────────────────────
function VolatilitySection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const v = D.volatility;
  return (
    <SectionCard label="Volatility Structure" freshness={D._freshness?.volatility} lens={v.lens} onLensPathway={onLensPathway}>
      <div className="di-vol-grid">
        {v.grid.map((c, i) => (
          <div key={i} className="di-vc">
            <div className="di-vc-l">{c.label}</div>
            <div className={`di-vc-v ${c.tone}`}>{c.value}</div>
            <div className={`di-vc-c ${c.dir}`}>{c.change}</div>
          </div>
        ))}
      </div>
      <div className={`di-struct-tag ${v.structure.kind}`}>{v.structure.label}</div>
      {v.rows.map((r, i) => (
        <div key={i} className="di-dr">
          <div className="di-dl">{r.label}</div>
          <div className={`di-dv ${r.tone}`}>{r.value}</div>
        </div>
      ))}
    </SectionCard>
  );
}

// ── 05b — Sentiment ────────────────────────────────────────
function SentimentSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const s = D.sentiment;
  return (
    <SectionCard label="Sentiment & Positioning" freshness={D._freshness?.sentiment} time={D._freshness?.sentiment ? undefined : 'Weekly updated'} lens={s.lens} onLensPathway={onLensPathway}>
      <div className="di-sec-lbl">CNN Fear &amp; Greed</div>
      <div className="di-sg-row">
        <div className="di-sg-lbl">{s.fearGreed.leftLabel}</div>
        <div className="di-sg-v">{s.fearGreed.value} — {s.fearGreed.label}</div>
        <div className="di-sg-lbl">{s.fearGreed.rightLabel}</div>
      </div>
      <div className="di-sg-wrap">
        <div className="di-sg-needle" style={{ left: `${s.fearGreed.value}%` }} />
      </div>
      <div className="di-sg-scale">
        {[0, 25, 50, 75, 100].map(n => <span key={n} className="di-sg-s">{n}</span>)}
      </div>

      <SubBlock title={null} marginTop={10}>
        {s.aaii.map((r, i) => (
          <div key={i} className="di-dr">
            <div className="di-dl">{r.label}</div>
            <div className={`di-dv ${r.tone}`}>{r.value}</div>
          </div>
        ))}
      </SubBlock>

      <SubBlock title="COT — Futures Positioning" marginTop={8}>
        {s.cot.map((r, i) => (
          <div key={i} className="di-dr">
            <div className="di-dl">{r.label}</div>
            <div className={`di-dv ${r.tone}`}>{r.value}</div>
          </div>
        ))}
      </SubBlock>

      <SubBlock title="Short Interest" marginTop={8}>
        {s.short.map((r, i) => (
          <div key={i} className="di-dr">
            <div className="di-dl">{r.label}</div>
            <div className={`di-dv ${r.tone}`}>{r.value}</div>
          </div>
        ))}
      </SubBlock>
    </SectionCard>
  );
}
function SubBlock({ title, marginTop, children }) {
  return (
    <div style={{
      marginTop: marginTop || 0, paddingTop: 8,
      borderTop: '1px solid #1e1e2a',
    }}>
      {title && <div className="di-sec-lbl">{title}</div>}
      {children}
    </div>
  );
}

// ── 06a — Rates & Credit ───────────────────────────────────
function RatesSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const r = D.rates;
  return (
    <SectionCard label="Rates & Credit" freshness={D._freshness?.rates} lens={r.lens} onLensPathway={onLensPathway}>
      <div className="di-sec-lbl">Yield Curve</div>
      {r.yieldCurve.map((y, i) => (
        <div key={i} className="di-yv-row">
          <div className="di-yv-term">{y.term}</div>
          <div className="di-yv-bw">
            <div className="di-yv-b" style={{ width: `${y.pct}%` }} />
          </div>
          <div className="di-yv-r">{y.rate}</div>
          <div className={`di-yv-c ${y.dir}`}>{y.change}</div>
        </div>
      ))}
      <div style={{ marginTop: 8 }}>
        {r.rows.map((row, i) => (
          <div key={i} className="di-dr">
            <div className="di-dl">{row.label}</div>
            <div className={`di-dv ${row.tone}`}>{row.value}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ── 06b — Currency ─────────────────────────────────────────
function CurrencySection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const c = D.currency;
  return (
    <SectionCard label="Currency" live lens={c.lens} onLensPathway={onLensPathway}>
      {c.rows.map((r, i) => (
        <div key={i} className="di-dr">
          <div className="di-dl">{r.label}</div>
          <div className={`di-dv ${r.tone}`}>
            {r.value}
            <span className={`di-dc ${r.dir}`}>{r.change}</span>
          </div>
        </div>
      ))}
      <div style={{
        marginTop: 8, paddingTop: 6,
        borderTop: '1px solid #1e1e2a',
      }}>
        <div style={{
          fontFamily: 'DM Mono, monospace', fontSize: 9,
          color: c.summaryTone === 'sage' ? '#7a9e82' : c.summaryTone === 'gold' ? '#c8a96e' : '#c8c4e0',
        }}>
          {c.summary}
        </div>
      </div>
    </SectionCard>
  );
}

// ── 06c — Commodities ──────────────────────────────────────
function CommoditiesSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const c = D.commodities;
  return (
    <SectionCard label="Commodities — Macro Signals" live lens={c.lens} onLensPathway={onLensPathway}>
      {c.rows.map((r, i) => (
        <div key={i} className="di-dr">
          <div className="di-dl">{r.label}</div>
          <div className={`di-dv ${r.tone}`}>
            {r.value}
            <span className={`di-dc ${r.dir}`}>{r.change}</span>
          </div>
        </div>
      ))}
      <div style={{
        marginTop: 8, paddingTop: 6,
        borderTop: '1px solid #1e1e2a',
      }}>
        <div style={{
          fontFamily: 'DM Mono, monospace', fontSize: 9,
          color: c.summaryTone === 'sage' ? '#7a9e82' : c.summaryTone === 'gold' ? '#c8a96e' : '#c8c4e0',
        }}>
          {c.summary}
        </div>
      </div>
    </SectionCard>
  );
}

// ── 07 — Economic Scoreboard ───────────────────────────────
function ScoreboardSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const s = D.scoreboard;
  return (
    <div className="di-section" style={{ marginBottom: 10 }}>
      <div className="di-sec-header">
        <div className="di-sec-label">Economic Scoreboard — Current State</div>
        <div className="di-sec-right">
          <div className="di-sec-time">As of last releases</div>
        </div>
      </div>
      <div className="di-sec-body" style={{ padding: 0 }}>
        <div className="di-score-grid">
          {s.cells.map((c, i) => (
            <div key={i} className="di-sc-cell">
              <div className="di-sc-lbl">{c.label}</div>
              <div className={`di-sc-v ${c.tone}`}>{c.value}</div>
              <div className="di-sc-trend">
                <div className={`di-sc-td ${c.trend.tone}`} />
                <div className="di-sc-tl">{c.trend.label}</div>
              </div>
              <div className="di-sc-ctx">{c.ctx}</div>
            </div>
          ))}
        </div>
      </div>
      <PractitionerLens lens={s.lens} onPathway={onLensPathway} />
    </div>
  );
}

// ── 08a — Intelligence Feed ────────────────────────────────
function FeedSection({ onLensPathway }) {
  const D = useContext(BundleContext);
  const items = D.feed.items;
  const fresh = D._freshness?.feed;
  const isLive = fresh?.source === 'live' && !fresh.isStale;
  return (
    <div className="di-section">
      <div className="di-sec-header">
        <div className="di-sec-label">Intelligence Feed</div>
        <div className="di-sec-right">
          {isLive && <div className="di-sec-live">Live</div>}
          <div className="di-sec-time">
            {fresh?.source === 'live' && fresh.fetchedAt
              ? `Updated ${formatFeedTime(fresh.fetchedAt)} · ${items.length} signals`
              : `${items.length} signals · Sample`}
          </div>
        </div>
      </div>
      <div className="di-sec-body" style={{ padding: '0 16px' }}>
        {items.map(it => <FeedItem key={it.id} item={it} onLensPathway={onLensPathway} />)}
      </div>
    </div>
  );
}
function formatFeedTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch (_e) { return ''; }
}
function FeedItem({ item, onLensPathway }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="di-fi">
      <div className={`di-fi-dot ${item.dot}`} />
      <div className="di-fi-body">
        <div className="di-fi-src">{item.source}</div>
        <div className="di-fi-txt" dangerouslySetInnerHTML={{ __html: item.body }} />
        <div className="di-fi-meta">
          <span className="di-fi-time">{item.time}</span>
          {item.lens && (
            <span className="di-fi-lens-tag" onClick={() => setOpen(o => !o)}>
              Practitioner Lens
            </span>
          )}
        </div>
        {item.lens && open && (
          <div className="di-fi-lens-body">
            <div className="di-fi-lens-txt" dangerouslySetInnerHTML={{ __html: item.lens.body }} />
            {item.lens.pathways && (
              <div className="di-fi-pws">
                {item.lens.pathways.map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`di-pw ${p}`}
                    style={{ fontSize: 8, padding: '4px 9px' }}
                    onClick={() => onLensPathway?.(p, item.lens)}
                  >
                    {item.lens.pathwayLabels?.[p] || p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 08b — Community Pulse (compact, on Dashboard) ──────────
function CommunitySection() {
  const D = useContext(BundleContext);
  const TYPE_LABEL = { disc: 'Discussion', art: 'Field Article', qa: 'Q&A' };
  return (
    <div className="di-section">
      <div className="di-sec-header">
        <div className="di-sec-label">Community Pulse</div>
        <div className="di-sec-right">
          <div className="di-sec-live">● {D.community.activeCount} active</div>
          <div className="di-sec-time" style={{ cursor: 'pointer' }}>View all →</div>
        </div>
      </div>
      {D.community.rows.map((row, ri) => (
        <div key={ri} className="di-community-grid" style={{ borderTop: ri > 0 ? '1px solid #1e1e2a' : 'none' }}>
          {row.map((c, ci) => (
            <div key={ci} className="di-ci">
              <div className={`di-ci-type ${c.kind}`}>{TYPE_LABEL[c.kind]}</div>
              <div className="di-ci-text" dangerouslySetInnerHTML={{ __html: c.text }} />
              <div className="di-ci-meta">{c.meta}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
